import { css } from '@emotion/react';
import { t } from 'i18n';
import type { BoundFlight, Segment } from 'shared';

import { getFlightDateAndTime, getFlightLayoverDuration, getFlightStopText } from '../utils';

import { theme } from './theme';

const styles = {
  container: css`
    display: flex;
    padding-bottom: ${theme.spacing.sm};
    padding-top: ${theme.spacing.sm};
  `,
  durationBarContainer: css`
    margin-right: 12px;
  `,
  flightContainer: css`
    @media (max-width: ${theme.breakpoints.desktop}) {
      display: flex;
      width: 100%;
    }
  `,
  flightContainerWrapper: (stops: number) => css`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    gap: ${stops > 1 ? theme.spacing.lg : theme.spacing.sm};
    width: 100%;
  `,
  layoverStopWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    z-index: 1;
    width: 100%;
    margin-left: -17px;
    border-left: 2px solid ${theme.colors.gray[2]};
  `,
  hopOnStopWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    z-index: 1;
    width: 100%;
    margin-left: -17px;
    border-left: 2px solid ${theme.colors.blue[1]};
  `,
  layoverStopLineTop: css`
    width: 8px;
    height: 2px;
    background-color: ${theme.colors.blue[1]};
    margin-left: -5px;
  `,
  layoverStopLineBottom: css`
    width: 8px;
    height: 2px;
    background-color: ${theme.colors.blue[1]};
    margin-left: -5px;
  `,
  layoverStopContainer: css`
    display: flex;
    flex-direction: column;
    font-size: ${theme.fontSize.sm};
    width: 100%;
    padding: 8px 8px 8px 17px;
    background-color: ${theme.colors.gray[0]};
  `,
  flightDateContainer: css`
    font-size: ${theme.fontSize.sm};
    color: ${theme.colors.gray[3]};
  `,
  durationBarCart: (flightHasLayover: boolean) => css`
    border-right: 2px solid ${theme.colors.blue[1]};
    margin: 0 3px;
    height: ${flightHasLayover ? '90%' : '85%'};
  `,
  durationBarDotBottom: css`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.colors.blue[1]};
  `,
  durationBarDotTop: css`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${theme.colors.blue[1]};
  `,
  airportCode: css`
    font-weight: ${theme.fontWeight.black};
  `,
  nonMarginBottom: css`
    margin-bottom: 0;
  `,
  xsSpan: css`
    font-size: ${theme.fontSize.sm};
  `,
};

type Props = {
  bound: BoundFlight | BoundFlight;
  stops: number;
};

export function BoundFlightComponent({ bound, stops }: Props) {
  const { startDateTimeInLocal, endDateTimeInLocal } = bound || {};
  const formattedTime = getFlightDateAndTime({
    departureTimeLocal: startDateTimeInLocal,
    arrivalTimeLocal: endDateTimeInLocal,
  });
  const flightHasLayover = stops > 1;

  const { flightDepartureDate, flightArrivalDate, departureTime, arrivalTime } = formattedTime;

  return (
    <div css={styles.container}>
      <div css={styles.durationBarContainer}>
        <div css={styles.durationBarDotTop} />
        <div css={styles.durationBarCart(flightHasLayover)} />
        <div css={styles.durationBarDotBottom} />
      </div>
      <div css={styles.flightContainer}>
        <div css={styles.flightContainerWrapper(stops)}>
          {bound && (
            <>
              <div>
                <p css={styles.nonMarginBottom}>
                  {bound.originCity.name} <span css={styles.airportCode}>{bound.origin.code}</span>
                </p>
                <div css={styles.flightDateContainer}>
                  {flightDepartureDate} {departureTime} ({getFlightStopText(bound.stops)}, {bound.connectionDuration})
                </div>
              </div>
              {bound.segments.map((segment, index) => {
                const isHopOnSegment = (segment.via?.length ?? 0) > 0;
                return isHopOnSegment ? (
                  <div css={styles.hopOnStopWrapper} key={segment.flightNumber}>
                    <div css={styles.layoverStopContainer} key={`hopon-${segment.arrivalAirport.code}`}>
                      <span css={styles.xsSpan}>
                        {segment.via?.[0].name} <span css={styles.airportCode}>{segment.via?.[0].code}</span>
                        <br /> {t('noAircraftChange')}
                        <br /> {t('common:stoppingTime')}: {segment.via?.[0].haltDuration}
                      </span>
                    </div>
                  </div>
                ) : (
                  index !== bound.segments.length - 1 && (
                    <div css={styles.layoverStopWrapper} key={segment.flightNumber}>
                      <span css={styles.layoverStopLineTop} />
                      <div css={styles.layoverStopContainer} key={segment.arrivalAirport.code}>
                        <span css={styles.xsSpan}>
                          {segment.arrivalCity.name} <span css={styles.airportCode}>{segment.arrivalAirport.code}</span>
                          <br /> {t('common:timeBetweenFlights')}:{' '}
                          {getFlightLayoverTimeHelper(segment, bound.segments[index + 1])}
                        </span>
                      </div>
                      <span css={styles.layoverStopLineBottom} />
                    </div>
                  )
                );
              })}

              <div>
                <p css={styles.nonMarginBottom}>
                  {bound.destinationCity.name} <span css={styles.airportCode}>{bound.destination.code}</span>
                </p>
                <div css={styles.flightDateContainer}>
                  {flightArrivalDate}, {arrivalTime}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function getFlightLayoverTimeHelper(currentSegment: Segment, nextSegment: Segment) {
  const layoverDuration = getFlightLayoverDuration(
    currentSegment.arrivalDateTimeInGmt,
    nextSegment.departureDateTimeInGmt
  );
  const hours = layoverDuration.hours;
  const minutes = layoverDuration.minutes;
  return <span>{t('formattedTime', { hours, minutes })}</span>;
}
